/**
 * Region helper for Contentful Page "Regions" field.
 * Used to derive current region from locale for region-based page resolution.
 */

/** Valid region codes in Contentful Page model (must match CMS validation list). */
export const CONTENTFUL_REGIONS = ['AU', 'NZ', 'SG', 'MY', 'CA', 'US', 'UK', 'Global'];

/** Value meaning "page applies to all regions". */
export const REGION_GLOBAL = 'Global';

const LOCALE_REGION_ALIAS = {
  GB: 'UK',
};

/**
 * Derives region code from locale for page filtering.
 * Examples: "en-AU" -> "AU", "en-NZ" -> "NZ", "fr-CA" -> "CA", "en-GB" -> "UK".
 * kl-my uses Contentful locale "en" (no country suffix per TSD); pass siteId "kl-my" to get "MY".
 *
 * @param {string} [locale] - Locale id (e.g. from ContentfulService), e.g. "en-AU"
 * @param {string} [siteId] - Current site (e.g. "kl-my") when locale alone does not encode region
 * @returns {string|null} Region code (AU, NZ, SG, MY, CA, US, UK) or null if not determinable
 */
export function getRegionFromLocale(locale, siteId) {
  if (!locale || typeof locale !== 'string') {
    return null;
  }
  const normalized = locale.replace(/_/g, '-');
  if (siteId === 'kl-my' && normalized.toLowerCase() === 'en') {
    return 'MY';
  }
  const part = normalized.split('-').pop();
  if (!part) {
    return null;
  }
  const region = LOCALE_REGION_ALIAS[part.toUpperCase()] ?? part.toUpperCase();
  return CONTENTFUL_REGIONS.includes(region) && region !== REGION_GLOBAL ? region : null;
}

/** Priority: exact match (1) > Global (2) > no regions (3) > no match (0). */
function getPageRegionPriority(page, currentRegion) {
  const regions = page.regions;
  if (!Array.isArray(regions) || regions.length === 0) {
    return 3;
  }
  if (regions.includes(currentRegion)) {
    return 1;
  }
  if (regions.includes(REGION_GLOBAL)) {
    return 2;
  }
  return 0;
}

/** AND filter for pageCollection queries — appends region visibility (KL-925). */
export function pageWhere(clauses, locale, siteId) {
  const currentRegion = getRegionFromLocale(locale, siteId);
  const visibleRegions = currentRegion ? [currentRegion, REGION_GLOBAL] : [REGION_GLOBAL];

  const regionFilter = {
    OR: [
      {regions_contains_some: visibleRegions},
      {regions_exists: false}
    ],
  };

  return {
    AND: [...clauses, regionFilter],
  };
}

/**
 * Picks the page that matches the current region from a list of pages (same slug).
 * Priority: exact region match > Global > no regions set (backward compat) > null.
 *
 * @param {Array<{regions?: string[]}>} items - Page entries from pageCollection (same slug)
 * @param {string|null} currentRegion - Current region code (e.g. "AU") or null
 * @returns {Object|null} Matching page or null
 */

export function selectPageByRegion(items, currentRegion) {
  if (!items?.length) {
    return null;
  }
  if (!currentRegion) {
    return (
      items.find((page) => {
        const regions = page?.regions;
        return !Array.isArray(regions) || regions.length === 0 || regions.includes(REGION_GLOBAL);
      }) ?? null
    );
  }
  let best = null;
  let bestPriority = 0;
  for (const page of items) {
    const priority = getPageRegionPriority(page, currentRegion);
    if (priority > 0 && (best === null || priority < bestPriority)) {
      best = page;
      bestPriority = priority;
    }
  }
  return best;
}
